import React, {useState, useEffect} from 'react'
import {SubmitHandler, useForm} from 'react-hook-form'
import {Button, Flex, Box} from '../../../vanilla'
import {CompetitionMarketingFormFields as ICompetitionMarketingFormFields, MarketingFormViewState, CompetitionMarketingFormCmsContent} from '../../../types/cms/competition-marketing-form'
import {Modal} from '../../modal'
import {CloseIcon} from '../../icons'
import { Cookies } from '../../../utils/storages'
import { getStepperConfiguration } from '../../../utils/cms/competition-marketing-form-utils'
import { CompetitionMarketingFormContent } from './competition-marketing-form-content'
import { useGenerateLead } from '../../../analytics/useGenerateLead'
import { DateUtils } from '../../../utils/date-utils'

export const CompetitionMarketingForm = React.forwardRef((props: CompetitionMarketingFormCmsContent, ref: React.Ref<HTMLDivElement>) => {
  const { content, formType = 'Page', isBanner, ...rest } = props
  const { formConfiguration } = content

  const [formViewState, setFormViewState] = useState<MarketingFormViewState>(MarketingFormViewState.INITIAL)
  const [isLoading, setIsLoading] = useState(true)
  const [isPreviouslySubmitted, setIsPreviouslySubmitted] = useState(false)
  const [currentStep, setCurrentStep] = useState(1)
  const [isValidating, setIsValidating] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const stepConfig = getStepperConfiguration(content)
  const totalSteps = stepConfig.totalSteps
  
  const previouslySubmitted = Cookies.get(formConfiguration?.reason)
  const hasCompetitionEnded = DateUtils.isBefore(new Date(formConfiguration?.formClosingDate), new Date())
  const generateLead = useGenerateLead()
  const isModal = formType === 'Modal'
  
  useEffect(() => {
    if (previouslySubmitted !== undefined || formViewState === MarketingFormViewState.SUCCESS) {
      setIsLoading(false)
      setIsPreviouslySubmitted(true)
    }
  }, [formViewState, previouslySubmitted])

  useEffect(() => {
    let formStatus = 'Viewed'

    if (hasCompetitionEnded) {
      formStatus = 'Closed'
      setIsLoading(false)
      setFormViewState(MarketingFormViewState.ENDED)
    }

    if (previouslySubmitted) {
      formStatus = 'Already Entered'
    }
    
    generateLead({
      form_id: formConfiguration?.reason,
      form_name: content.title,
      form_status: formStatus,
      form_type: formType
    })
  }, [])

  const form = useForm<ICompetitionMarketingFormFields>({
    mode: 'onBlur',
    defaultValues: {
      email: '',
      firstname: '',
      lastname: '',
      phone: '',
      address: '',
      searchForAddress: '',
      address1: '',
      address2: '',
      city: '',
      postalCode: '',
      marketingPermission1: false,
      marketingPermission2: false
    }
  })

  const handleCompetitionMarketingFormSubmission: SubmitHandler<ICompetitionMarketingFormFields> = async (formData) => {
    try {
      if (!formConfiguration?.reason) throw new Error('Reason is required in form settings')
      setIsSubmitting(true)
      setIsLoading(true)
      const response = await fetch('/api/marketing-form/submit', {
        method: 'POST',

        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          formData,
          reason: formConfiguration.reason
        })
      })

      generateLead({
        form_id: formConfiguration?.reason,
        form_name: content.title,
        form_status: response.ok ? 'Submitted' : 'Error',
        form_type: formType
      })

      if (!response.ok) {
        setFormViewState(MarketingFormViewState.ERROR)
        setIsLoading(false)
        throw new Error('Form submission failed')
      }

      //if successful submission, create cookie
      Cookies.set(formConfiguration.reason, `Entered ${formConfiguration.reason}`,  {expires : new Date(formConfiguration.formClosingDate)})
      setFormViewState(MarketingFormViewState.SUCCESS)
      setIsLoading(false)
      setIsSubmitting(false)
    } catch (error) {
      setFormViewState(MarketingFormViewState.ERROR)
      setIsLoading(false)
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setFormViewState(MarketingFormViewState.INITIAL)
    setCurrentStep(1)
  }

  const handleManualSubmit = async () => {
    setIsSubmitting(true)
    // First validate all form fields
    const isFormValid = await form.trigger()
    if (isFormValid) {
      const formData = form.getValues()
      await handleCompetitionMarketingFormSubmission(formData)
    } else {
      setIsSubmitting(false)
    }
  }

  const handleNext = async () => {
    setIsValidating(true)
    
    // Get the current step's fields that need to be validated
    const currentStepFields = stepConfig.steps[currentStep - 1]?.fields || []
    
    // Map step field names to actual form field names
    const formFieldsToValidate: (keyof ICompetitionMarketingFormFields)[] = []
    currentStepFields.forEach((field: string) => {
      if (field === 'checkboxes') {
        // Add validation for marketing permission checkboxes if they exist
        if (content.formCheckboxes?.firstCheckbox) {
          formFieldsToValidate.push('marketingPermission1' as keyof ICompetitionMarketingFormFields)
        }
        if (content.formCheckboxes?.displaySecondCheckbox) {
          formFieldsToValidate.push('marketingPermission2' as keyof ICompetitionMarketingFormFields)
        }
        return
      }
      formFieldsToValidate.push(field as keyof ICompetitionMarketingFormFields)
    })

    // Trigger validation for the current step's fields
    const isStepValid = await form.trigger(formFieldsToValidate)
    
    setIsValidating(false)
    
    if (isStepValid && currentStep < totalSteps) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const isLastStep = currentStep === totalSteps

  return isModal ? (
    <Modal
      size="full-md"
      closeIcon={false}
      isOpen={props.isOpen}
      onClose={props.onClose}
    >
      <Box position="relative">
        <Flex justify="flex-end" width="full" padding="16px">
          <Button variant="unstyled" onClick={() => props.onClose()} paddingX="0px" height="auto">
            <CloseIcon boxSize="28px" color="gray800" />
          </Button>
        </Flex>
        <Box paddingX="20px" paddingTop="4px" paddingBottom="44px">
          <CompetitionMarketingFormContent
            isModal={isModal ?? false}
            form={form}
            isPreviouslySubmitted={isPreviouslySubmitted}
            isLoading={isLoading}
            state={formViewState}
            onSubmit={form.handleSubmit(handleCompetitionMarketingFormSubmission)}
            handleClose={() => {
              handleClose()
              props.onClose()
            }}
            currentStep={currentStep}
            totalSteps={totalSteps}
            onNext={handleNext}
            onBack={handleBack}
            isLastStep={isLastStep}
            isValidating={isValidating}
            onManualSubmit={handleManualSubmit}
            stepConfig={stepConfig}
            isSubmitting={isSubmitting}
            {...content}
          />
        </Box>
      </Box>
    </Modal>
  ) : (
    <CompetitionMarketingFormContent
      isModal={isModal ?? false}
      form={form}
      isPreviouslySubmitted={isPreviouslySubmitted}
      isLoading={isLoading}
      state={formViewState}
      onSubmit={form.handleSubmit(handleCompetitionMarketingFormSubmission)}
      handleClose={() => {
        handleClose()
      }}
      currentStep={currentStep}
      totalSteps={totalSteps}
      onNext={handleNext}
      onBack={handleBack}
      isLastStep={isLastStep}
      isValidating={isValidating}
      onManualSubmit={handleManualSubmit}
      stepConfig={stepConfig}
      isSubmitting={isSubmitting}
      {...content}
      {...rest}
      ref={ref}
    />
  )
})